import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  getSettingToggleValue,
  getSettingRowWithToggle,
  setSettingToggleValue,
  openClickableSettingModal,
  getSettingModalSelectedPrivacyLevel,
  closeSettingModal,
  setSettingModalPrivacyLevel,
} from './fbSettingsUtils'
import { homeUrl, profileAndTaggingSettingsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  getCantUndoFnFromKey,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Settings property functions
const currentValueFns = {
  [FacebookSettingKey.REVIEW_POSTS]: getCurrentValueFnFromKey(
    FacebookSettingKey.REVIEW_POSTS,
    (value) => (value ? 'On' : 'Off'),
  ),
  [FacebookSettingKey.REVIEW_TAGS]: getCurrentValueFnFromKey(
    FacebookSettingKey.REVIEW_TAGS,
    (value) => (value ? 'On' : 'Off'),
  ),
  [FacebookSettingKey.TAGGED_VISIBILITY]: getCurrentValueFnFromKey(
    FacebookSettingKey.TAGGED_VISIBILITY,
    (value) => (value ? value.name : null),
  ),
}

const isFulfilledFns = {
  [FacebookSettingKey.REVIEW_POSTS]: getIsFulfilledFnFromKey(
    FacebookSettingKey.REVIEW_POSTS,
    (value) => value,
  ),
  [FacebookSettingKey.REVIEW_TAGS]: getIsFulfilledFnFromKey(
    FacebookSettingKey.REVIEW_TAGS,
    (value) => value,
  ),
  [FacebookSettingKey.TAGGED_VISIBILITY]: getIsFulfilledFnFromKey(
    FacebookSettingKey.TAGGED_VISIBILITY,
    (value) => (value ? value.name === 'Only me' : null),
  ),
}

const cantUndoFns = {
  [FacebookSettingKey.REVIEW_POSTS]: defaultCantUndoFn,
  [FacebookSettingKey.REVIEW_TAGS]: defaultCantUndoFn,
  [FacebookSettingKey.TAGGED_VISIBILITY]: getCantUndoFnFromKey(
    FacebookSettingKey.TAGGED_VISIBILITY,
    (value) =>
      value ? !selectAudienceUtils.isPrivacySettingUndoable(value) : false,
  ),
}

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

const getCurrentValueFn = (key) => currentValueFns[key]
const getIsFulfilledFn = (key) => isFulfilledFns[key]
const getCantUndoFn = (key) => cantUndoFns[key]

// Constant associating each setting with expeted label text

const _SETTINGS = {
  REVIEW_POSTS: 'Review posts',
  REVIEW_TAGS: 'Review tags',
  TAGGED_VISIBILITY: 'Who can see posts',
}

enum StageKey {
  INIT_SETTING_WHAT_YOURE_TAGGED_IN = 'INIT_SETTING_WHAT_YOURE_TAGGED_IN',
  GATHER_SETTING_WHAT_YOURE_TAGGED_IN = 'GATHER_SETTING_WHAT_YOURE_TAGGED_IN',
  UPDATE_SETTING_WHAT_YOURE_TAGGED_IN = 'UPDATE_SETTING_WHAT_YOURE_TAGGED_IN',
  UNDO_SETTING_WHAT_YOURE_TAGGED_IN = 'UNDO_SETTING_WHAT_YOURE_TAGGED_IN',
  FINISH_SETTING_WHAT_YOURE_TAGGED_IN = 'FINISH_SETTING_WHAT_YOURE_TAGGED_IN',
}

const initStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.INIT_SETTING_WHAT_YOURE_TAGGED_IN,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: homeUrl,
    action: async () => {},
  },
]

const gatherStageDefs: StageDef[] = [
  ...initStageDefs,
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_WHAT_YOURE_TAGGED_IN,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([_SETTINGS.TAGGED_VISIBILITY])
      const taggedVisibilitySetting = await getSettingModalSelectedPrivacyLevel(
        _SETTINGS.TAGGED_VISIBILITY,
      )
      await closeSettingModal(_SETTINGS.TAGGED_VISIBILITY, 'CANCEL', {
        removeModalManually: true,
      })

      const reviewTagRow = await getSettingRowWithToggle(_SETTINGS.REVIEW_TAGS)
      const reviewPostsRow = await getSettingRowWithToggle(
        _SETTINGS.REVIEW_POSTS,
      )

      updateStoredInitialValues(
        {
          [FacebookSettingKey.TAGGED_VISIBILITY]: taggedVisibilitySetting,
          [FacebookSettingKey.REVIEW_TAGS]:
            await getSettingToggleValue(reviewTagRow),
          [FacebookSettingKey.REVIEW_POSTS]:
            await getSettingToggleValue(reviewPostsRow),
        },
        FBRecommendationKey.FB_WHAT_YOURE_TAGGED_IN,
      )

      return true
    },
  },
]

// Execution code

const updateStageDefs: StageDef[] = [
  ...initStageDefs,
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_WHAT_YOURE_TAGGED_IN,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return false
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.REVIEW_POSTS,
          getIsFulfilledFn,
        )
      ) {
        const reviewPostsRow = await getSettingRowWithToggle(
          _SETTINGS.REVIEW_POSTS,
        )
        const reviewPostsSetting = await getSettingToggleValue(reviewPostsRow)

        if (!reviewPostsSetting && !isDry) {
          await setSettingToggleValue(reviewPostsRow, true)
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.REVIEW_TAGS,
          getIsFulfilledFn,
        )
      ) {
        const reviewTagsRow = await getSettingRowWithToggle(
          _SETTINGS.REVIEW_TAGS,
        )
        const reviewTagsSetting = await getSettingToggleValue(reviewTagsRow)

        if (!reviewTagsSetting && !isDry) {
          await setSettingToggleValue(reviewTagsRow, true)
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.TAGGED_VISIBILITY,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([_SETTINGS.TAGGED_VISIBILITY])
        await setSettingModalPrivacyLevel(
          _SETTINGS.TAGGED_VISIBILITY,
          isDry,
          selectAudienceUtils.ONLY_ME,
        )
      }
      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  ...initStageDefs,
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_WHAT_YOURE_TAGGED_IN,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!initialValues) {
        return false
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.REVIEW_POSTS,
          getIsFulfilledFn,
        )
      ) {
        const reviewPostsRow = await getSettingRowWithToggle(
          _SETTINGS.REVIEW_POSTS,
        )

        if (!isDry) {
          await setSettingToggleValue(
            reviewPostsRow,
            initialValues.REVIEW_POSTS,
          )
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.REVIEW_TAGS,
          getIsFulfilledFn,
        )
      ) {
        const reviewTagsRow = await getSettingRowWithToggle(
          _SETTINGS.REVIEW_TAGS,
        )

        if (!isDry) {
          await setSettingToggleValue(reviewTagsRow, initialValues.REVIEW_TAGS)
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.TAGGED_VISIBILITY,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([_SETTINGS.TAGGED_VISIBILITY])
        await setSettingModalPrivacyLevel(
          _SETTINGS.TAGGED_VISIBILITY,
          isDry,
          initialValues[FacebookSettingKey.TAGGED_VISIBILITY],
        )
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
